import { FaChevronDown } from '@react-icons/all-files/fa/FaChevronDown';
import { RiInformationLine } from '@react-icons/all-files/ri/RiInformationLine';
import cls from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import useLocalStorageState from 'use-local-storage-state';
import { Button } from '@/components/atoms/Button/Button';
import { Ul } from '@/components/atoms/List/List';
import { H2, Li, Span } from '@/components/atoms/Typography/Typography';
import { AccordionSingle } from '@/components/molecules/Accordion/Accordion';
import { AccordionItem } from '@/components/molecules/AccordionItem/AccordionItem';
import { ModalDrawerDialog } from '@/components/molecules/ModalDrawerDialog/ModalDrawerDialog';
import nameSpacesConfig from '@/config/config.nameSpaces.json';
import { FormValues } from '@/containers/ConfigurePage/components/FormConfig/formData';
import { getSelectedSubscription } from '@/containers/ConfigurePage/utils/getSelectedSubscription';
import { getCountryConfigFromLocale } from '@/lib/i18n/getCountryConfigFromLocale';
import { useData } from '@/lib/pageData/pageDataContext';
import { SubscriptionAddon } from '@/types/Subscription';
import { TransformedAssetCategoryResult } from '@/types/TransformedAssetCategoryResults';
import { ProductSubscriptionByRegionResponse } from '@/utils/fetch/fetchProductSubscriptionByRegion';
import { formatPrice } from '@/utils/formatPrice';
import { getFormerSignUpFee } from '@/utils/getFormerSignUpFee';
import { getOneOffFlexiblePrice } from '@/utils/getOneOffFlexiblePrice';
import { getSubscriptionAddon } from '@/utils/getSubscriptionAddon';
import { isThreeTierProduct } from '@/utils/threeTier';
import { BASKET_STORAGE_KEY } from '../../constants';
import { useStudentDiscount } from '../../hooks/useStudentDiscount';
import styles from './CostSummary.module.scss';

interface Props {
  product: TransformedAssetCategoryResult;
  subscriptions: ProductSubscriptionByRegionResponse;
  subscriptionAddons?: SubscriptionAddon[];
}

const _isIntroWeeks = true;

export const CostSummary = ({
  product,
  subscriptionAddons,
  subscriptions,
}: Props) => {
  const { locale } = useRouter();
  const { currency } = useData<'default'>();
  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(false);
  const [isBasketOptIn] = useLocalStorageState<boolean>(BASKET_STORAGE_KEY);
  const { countryCode } = getCountryConfigFromLocale(locale!);
  const { control } = useFormContext<FormValues>();
  const { t } = useTranslation([
    nameSpacesConfig.pages,
    nameSpacesConfig.common,
  ]);
  const currentFormValues = useWatch<FormValues>({ control });
  const studentDiscount = useStudentDiscount(product, subscriptions);
  const [isOneOffModalOpen, setIsOneOffModalOpen] = useState(false);

  const currentSubscription = getSelectedSubscription({
    currentFormValues,
    product,
    subscriptions,
  });
  const formerSignUpFee = getFormerSignUpFee(
    countryCode,
    currentSubscription!.code
  );
  const formerRate = currentSubscription?.isSpecialOffer
    ? subscriptions.find(
        sub =>
          sub.customerType === currentSubscription.customerType &&
          sub.isFlexible === currentSubscription.isFlexible &&
          sub.minDurationMonths === currentSubscription.minDurationMonths &&
          !sub.isSpecialOffer
      )?.rate
    : undefined;
  const isThreeTier = isThreeTierProduct(
    countryCode,
    product.assetCategoryCode
  );

  const basketAddon = getSubscriptionAddon('basket', subscriptionAddons);
  const showBasketAddonInfo = Boolean(isBasketOptIn && basketAddon);

  return (
    <div className={cls(styles.container)} data-test-id="cost-summary">
      <H2 className={styles.heading} version="level3">
        {t('configure.costSummary.headingMain')}
      </H2>
      <AccordionSingle name="costs" collapsible={showBasketAddonInfo}>
        <AccordionItem
          className={styles.accordionItem}
          value="costs"
          title={
            <Ul className={cls(styles.list, styles.titleList)}>
              <Li onClick={() => setIsPriceDetailsOpen(!isPriceDetailsOpen)}>
                <Span>{t('configure.costSummary.monthly')}</Span>
                <Span
                  className={cls(styles.monthlyPrice, {
                    [styles.offest]: showBasketAddonInfo,
                  })}
                >
                  {!!(
                    formerRate ||
                    (currentFormValues.student === 'yes' && studentDiscount)
                  ) && (
                    <Span className={styles.strikePrice} color="strike_through">
                      {formatPrice(
                        formerRate || studentDiscount?.normalRate,
                        locale!,
                        currency
                      )}
                    </Span>
                  )}
                  {/* Wrap text nodes in tags if the text node is not the only child of its parent,
          otherwise React will throw an error and the application will crash.
          See the link: https://github.com/facebook/react/issues/11538#issuecomment-390386520 */}
                  <Span className={styles.priceValue}>
                    <span>
                      {formatPrice(
                        currentSubscription && showBasketAddonInfo
                          ? currentSubscription?.rate + basketAddon!.price
                          : currentSubscription?.rate,
                        locale!,
                        currency
                      )}
                    </span>
                    {!!formerRate && <span>*</span>}
                    {showBasketAddonInfo && (
                      <FaChevronDown
                        className={cls(styles.chevronDown, {
                          [styles.expanded]: isPriceDetailsOpen,
                        })}
                      />
                    )}
                  </Span>
                </Span>
              </Li>
            </Ul>
          }
          name="monthlyCost"
        >
          {showBasketAddonInfo && (
            <Ul
              className={cls(styles.list, {
                [styles.sublist]: !!showBasketAddonInfo,
              })}
            >
              <Li>
                <Span>{product.productConfig.uiName}</Span>
                <Span
                  className={cls(styles.price, {
                    [styles.offest]: showBasketAddonInfo,
                  })}
                >
                  {!!(
                    formerRate ||
                    (currentFormValues.student === 'yes' && studentDiscount)
                  ) && (
                    <Span className={styles.strikePrice} color="strike_through">
                      {formatPrice(
                        formerRate || studentDiscount?.normalRate,
                        locale!,
                        currency
                      )}
                    </Span>
                  )}
                  {/* Wrap text nodes in tags if the text node is not the only child of its parent,
             otherwise React will throw an error and the application will crash.
             See the link: https://github.com/facebook/react/issues/11538#issuecomment-390386520 */}
                  <span>
                    {formatPrice(currentSubscription?.rate, locale!, currency)}
                  </span>
                </Span>
              </Li>
              <Li>
                <Span>{t('pages:configure.basket.label')}</Span>
                <Span
                  className={cls(styles.price, {
                    [styles.offest]: showBasketAddonInfo,
                  })}
                >
                  {formatPrice(basketAddon?.price, locale!, currency)}
                </Span>
              </Li>
            </Ul>
          )}
        </AccordionItem>
      </AccordionSingle>
      <Ul className={cls(styles.list)}>
        {/* TEMPORARILY SOLUTION FOR INTRO WEEKS - Disabled delivery cost  */}
        {!_isIntroWeeks ? (
          <Li>
            <Span>{t('configure.costSummary.delivery')}</Span>
            <Span className={styles.price}>
              {formatPrice(0, locale!, currency)}
            </Span>
          </Li>
        ) : null}
        {!isThreeTier ? (
          <>
            <Li>
              <Span>
                {t('configure.costSummary.oneOff')}{' '}
                <Button
                  aria-label={t('general.moreInfo')}
                  className={styles.oneOffInfoBtn}
                  onClick={() => setIsOneOffModalOpen(true)}
                >
                  <RiInformationLine size={20} />
                </Button>
              </Span>
              <Span
                className={cls(styles.price, {
                  [styles.offest]: showBasketAddonInfo,
                })}
              >
                {!currentSubscription?.isFlexible && (
                  <>
                    <Span color="strike_through">
                      {formatPrice(
                        getOneOffFlexiblePrice(
                          subscriptions,
                          currentSubscription
                        ),
                        locale!,
                        currency
                      )}
                    </Span>{' '}
                  </>
                )}
                {formatPrice(currentSubscription?.oneOffFee, locale!, currency)}
              </Span>
            </Li>
          </>
        ) : (
          <>
            <Li>
              <Span>
                {t('configure.costSummary.oneOff')}{' '}
                <Button
                  aria-label={t('general.moreInfo')}
                  className={styles.oneOffInfoBtn}
                  onClick={() => setIsOneOffModalOpen(true)}
                >
                  <RiInformationLine size={20} />
                </Button>
              </Span>
              <Span
                className={cls(styles.price, {
                  [styles.offest]: showBasketAddonInfo,
                })}
              >
                {formerSignUpFee ? (
                  <>
                    <Span className={styles.strikePrice} color="strike_through">
                      {formatPrice(formerSignUpFee, locale!, currency)}
                    </Span>
                    <Span>{formatPrice(0, locale!, currency)}</Span>
                  </>
                ) : (
                  formatPrice(currentSubscription?.oneOffFee, locale!, currency)
                )}
              </Span>
            </Li>
            <Li>
              <Span>{t('common:costSummary.serviceAndRepairs.title')}</Span>
              <Span
                className={cls(styles.price, {
                  [styles.offest]: showBasketAddonInfo,
                })}
              >
                {t('common:costSummary.serviceAndRepairs.priceLabel')}
              </Span>
            </Li>
          </>
        )}
      </Ul>
      <ModalDrawerDialog
        labels={{
          ariaLabel: t('common:modal.info.label'),
          closeButtonLabel: t('common:drawer.close'),
          submitButtonLabel: t('common:modal.info.submit'),
        }}
        isOpen={isOneOffModalOpen}
        onDismiss={() => setIsOneOffModalOpen(false)}
        heading={t('common:costSummary.priceBreakdown.oneOffFee.heading')}
      >
        {t('common:costSummary.priceBreakdown.oneOffFee.text')}
      </ModalDrawerDialog>
    </div>
  );
};
